// src/utils/ImageHoverEffect.js

const MEDIA_SELECTOR = [
  ".media-stage__layer--next img",
  ".media-stage__layer--next video",
  ".media-stage__layer--current img",
  ".media-stage__layer--current video",
  ".image-hover-media img",
  ".image-hover-media video",
  ".lazy-video video",
  "img",
  "video"
].join(", ");

const ZOOM_STORAGE_KEY = "imageZoomLevel";
const ZOOM_ENABLED_STORAGE_KEY = "imageZoomEnabled";
const DEFAULT_ZOOM = 1.5;
const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.15;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const getStoredZoomLevel = () => {
  const stored = parseFloat(sessionStorage.getItem(ZOOM_STORAGE_KEY));
  return Number.isFinite(stored)
    ? clamp(stored, MIN_ZOOM, MAX_ZOOM)
    : DEFAULT_ZOOM;
};

const getStoredZoomEnabled = () => {
  return sessionStorage.getItem(ZOOM_ENABLED_STORAGE_KEY) === "true";
};

const setGlobalZoomLevel = (value) => {
  const nextValue = clamp(value, MIN_ZOOM, MAX_ZOOM);
  document.documentElement.style.setProperty(
    "--image-zoom-level",
    String(nextValue)
  );
  sessionStorage.setItem(ZOOM_STORAGE_KEY, String(nextValue));
  return nextValue;
};

const setStoredZoomEnabled = (value) => {
  sessionStorage.setItem(ZOOM_ENABLED_STORAGE_KEY, value ? "true" : "false");
};

const getButtonMarkup = () => `
  <svg
    viewBox="0 0 24 24"
    width="1rem"
    height="1rem"
    aria-hidden="true"
    focusable="false"
  >
    <path
      fill="currentColor"
      d="M10 4a6 6 0 1 0 3.874 10.582l4.272 4.272 1.414-1.414-4.272-4.272A6 6 0 0 0 10 4Zm0 2a4 4 0 1 1 0 8a4 4 0 0 1 0-8Zm-1 1h2v2h2v2h-2v2H9v-2H7V9h2V7Z"
    />
  </svg>
`;

export const applyImageZoomEffect = (container) => {
  if (!container) return () => {};

  let activeMedia = null;
  let rafId = null;
  let activationRafId = null;
  let button = null;
  let zoomLevel = getStoredZoomLevel();
  let zoomEnabled = getStoredZoomEnabled();
  let lastPointer = null;

  setGlobalZoomLevel(zoomLevel);

  const clearMediaStyles = (node) => {
    if (!node) return;
    node.style.transform = "";
    node.style.transformOrigin = "";
    node.style.willChange = "";
    node.style.transition = "";
  };

  const ensureButton = () => {
    let nextButton = container.querySelector(".zoom-toggle-btn");

    if (!nextButton) {
      nextButton = document.createElement("button");
      nextButton.type = "button";
      nextButton.className = "zoom-toggle-btn";
      nextButton.innerHTML = getButtonMarkup();
      container.appendChild(nextButton);
    }

    nextButton.setAttribute("aria-label", "Toggle zoom");
    return nextButton;
  };

  const syncZoomUI = () => {
    container.setAttribute("data-zoom", zoomEnabled ? "on" : "off");

    if (!button) return;

    button.setAttribute("aria-pressed", zoomEnabled ? "true" : "false");
    button.setAttribute("title", zoomEnabled ? "Disable zoom" : "Enable zoom");
    button.setAttribute(
      "aria-label",
      zoomEnabled ? "Disable zoom" : "Enable zoom"
    );
  };

  const getActiveMedia = () => {
    const nextMedia = container.querySelector(MEDIA_SELECTOR);

    if (nextMedia !== activeMedia) {
      clearMediaStyles(activeMedia);
      activeMedia = nextMedia;

      if (activeMedia) {
        activeMedia.style.willChange = "transform";
        activeMedia.style.transition = "transform 0.12s ease-out";
        activeMedia.style.transformOrigin = "50% 50%";
      }
    }

    return activeMedia;
  };

  const applyZoomFromPoint = (clientX, clientY) => {
    if (!zoomEnabled) return;

    const media = getActiveMedia();
    if (!media) return;

    const rect = container.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const x = ((clientX - rect.left) / rect.width) * 100;
    const y = ((clientY - rect.top) / rect.height) * 100;

    media.style.transformOrigin = `${x}% ${y}%`;
    media.style.transform = `scale(${zoomLevel})`;
  };

  const applyStoredZoomImmediately = () => {
    if (!zoomEnabled) return;

    const media = getActiveMedia();
    if (!media) return;

    if (lastPointer) {
      applyZoomFromPoint(lastPointer.clientX, lastPointer.clientY);
      return;
    }

    media.style.transformOrigin = "50% 50%";
    media.style.transform = `scale(${zoomLevel})`;
  };

  const forceImmediateActivation = (event = null) => {
    if (event) {
      lastPointer = {
        clientX: event.clientX,
        clientY: event.clientY
      };
    }

    getActiveMedia();
    applyStoredZoomImmediately();

    if (activationRafId) {
      cancelAnimationFrame(activationRafId);
    }

    activationRafId = requestAnimationFrame(() => {
      getActiveMedia();
      applyStoredZoomImmediately();
    });
  };

  const resetZoom = () => {
    if (rafId) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }

    if (activationRafId) {
      cancelAnimationFrame(activationRafId);
      activationRafId = null;
    }

    const media = getActiveMedia();
    if (!media) return;

    media.style.transformOrigin = "50% 50%";
    media.style.transform = "scale(1)";
  };

  const handlePointerMove = (event) => {
    lastPointer = {
      clientX: event.clientX,
      clientY: event.clientY
    };

    if (!zoomEnabled) return;

    if (rafId) cancelAnimationFrame(rafId);

    const { clientX, clientY } = event;

    rafId = requestAnimationFrame(() => {
      applyZoomFromPoint(clientX, clientY);
    });
  };

  const handleWheel = (event) => {
    if (!zoomEnabled) return;

    event.preventDefault();

    lastPointer = {
      clientX: event.clientX,
      clientY: event.clientY
    };

    const delta = event.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP;
    zoomLevel = setGlobalZoomLevel(zoomLevel + delta);

    applyZoomFromPoint(event.clientX, event.clientY);
  };

  const toggleZoom = (event = null) => {
    zoomEnabled = !zoomEnabled;
    setStoredZoomEnabled(zoomEnabled);
    syncZoomUI();

    if (zoomEnabled) {
      forceImmediateActivation(event);
    } else {
      resetZoom();
    }
  };

  const handleDoubleClick = (event) => {
    event.preventDefault();
    toggleZoom(event);
  };

  const handleButtonClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    toggleZoom(lastPointer ? { ...lastPointer } : null);
  };

  const handleButtonDoubleClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    toggleZoom(lastPointer ? { ...lastPointer } : null);
  };

  button = ensureButton();
  syncZoomUI();
  getActiveMedia();
  applyStoredZoomImmediately();

  button.addEventListener("click", handleButtonClick);
  button.addEventListener("dblclick", handleButtonDoubleClick);

  const observer = new MutationObserver(() => {
    const previousMedia = activeMedia;
    getActiveMedia();

    if (activeMedia !== previousMedia && zoomEnabled) {
      forceImmediateActivation();
    }

    const existingButton = container.querySelector(".zoom-toggle-btn");

    if (existingButton !== button) {
      if (button) {
        button.removeEventListener("click", handleButtonClick);
        button.removeEventListener("dblclick", handleButtonDoubleClick);
      }

      button = ensureButton();
      button.addEventListener("click", handleButtonClick);
      button.addEventListener("dblclick", handleButtonDoubleClick);
      syncZoomUI();
    }
  });

  observer.observe(container, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["class", "src", "style"]
  });

  container.addEventListener("pointermove", handlePointerMove);
  container.addEventListener("pointerleave", resetZoom);
  container.addEventListener("pointercancel", resetZoom);
  container.addEventListener("dblclick", handleDoubleClick);
  container.addEventListener("wheel", handleWheel, { passive: false });

  return () => {
    if (rafId) cancelAnimationFrame(rafId);
    if (activationRafId) cancelAnimationFrame(activationRafId);

    observer.disconnect();

    if (button) {
      button.removeEventListener("click", handleButtonClick);
      button.removeEventListener("dblclick", handleButtonDoubleClick);
    }

    container.removeEventListener("pointermove", handlePointerMove);
    container.removeEventListener("pointerleave", resetZoom);
    container.removeEventListener("pointercancel", resetZoom);
    container.removeEventListener("dblclick", handleDoubleClick);
    container.removeEventListener("wheel", handleWheel);

    clearMediaStyles(activeMedia);
  };
};